import React from 'react';

// =============================================
// LIABILITIES SECTION COMPONENT
// =============================================

export function LiabilitiesSection({
    // Personal loans module
    personalLoans,
    setPersonalLoans,
    filteredPersonalLoans,
    // Gold loans module
    goldLoans,
    setGoldLoans,
    filteredGoldLoans,
    // Credit cards module
    creditCards,
    setCreditCards,
    filteredCreditCards,
    cardTotals,
    // Shared
    searchQuery,
    setSearchQuery,
    activeModule,
    // Modal handlers
    setModalType,
    setShowAddModal,
    setEditingItem,
    setShowEditModal,
    // Loan actions
    handleMarkLoanPaid,
    handleMarkGoldLoanPaid,
    // History expansion
    expandedLoanId,
    setExpandedLoanId,
    // Formatting
    baseCurrencyLabel,
    fmtBase,
    toBase,
    sgdRate,
    lkrRate
}) {
    return (
        <>
            {activeModule === 'personal' && (
                <>
                    <div className="search-container">
                        <div className="search-wrapper">
                            <span className="search-icon">🔍</span>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search loans..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    className="clear-search visible"
                                    onClick={() => setSearchQuery('')}
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="section-container">
                        <div className="section-header">
                            <div>
                                <div className="section-title">Personal Loans</div>
                                <div className="section-subtitle">{filteredPersonalLoans.length} active</div>
                            </div>
                            <button
                                className="add-btn"
                                onClick={() => {
                                    setModalType('personal-loan');
                                    setShowAddModal(true);
                                }}
                            >
                                + Add
                            </button>
                        </div>

                        {filteredPersonalLoans.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">💰</div>
                                <div className="empty-text">No personal loans found</div>
                            </div>
                        ) : (
                            filteredPersonalLoans.map(loan => {
                                const progress = (loan.principalPaid / loan.totalAmount) * 100;
                                // NG-2: derive whether this month's instalment has been recorded
                                const currentPeriod = new Date().toISOString().slice(0, 7);
                                const currentMonthPaid = (loan.paymentHistory || []).some(p => p.period === currentPeriod);
                                const isHistoryOpen = expandedLoanId === loan.id;

                                return (
                                    <div
                                        key={loan.id}
                                        className="item-card"
                                        onClick={() => {
                                            setModalType('personal-loan');
                                            setEditingItem(loan);
                                            setShowEditModal(true);
                                        }}
                                    >
                                        <div className="card-header">
                                            <div>
                                                <div className="card-title">{loan.name}</div>
                                                <div className="card-subtitle">{loan.type}</div>
                                            </div>
                                            <div className="card-badge">{loan.monthsLeft}M LEFT</div>
                                        </div>

                                        <div className="card-details">
                                            <div className="detail-item">
                                                <div className="detail-label">Total Amount</div>
                                                <div className="detail-value">{loan.currency} {loan.totalAmount.toLocaleString()}</div>
                                            </div>
                                            <div className="detail-item">
                                                <div className="detail-label">Monthly</div>
                                                <div className="detail-value">{loan.currency} {loan.monthlyInstallment.toLocaleString()}</div>
                                            </div>
                                            <div className="detail-item">
                                                <div className="detail-label">Paid</div>
                                                <div className="detail-value">{loan.currency} {loan.principalPaid.toLocaleString()}</div>
                                            </div>
                                            <div className="detail-item">
                                                <div className="detail-label">Remaining</div>
                                                <div className="detail-value">{loan.currency} {loan.principalRemaining.toLocaleString()}</div>
                                            </div>
                                            <div className="detail-item">
                                                <div className="detail-label">≈ {baseCurrencyLabel} Remaining</div>
                                                <div className="detail-value highlight">{fmtBase(toBase(loan.principalRemaining, loan.currency || 'SGD'))}</div>
                                            </div>
                                            <div className="detail-item">
                                                <div className="detail-label">≈ {baseCurrencyLabel} Monthly</div>
                                                <div className="detail-value">{fmtBase(toBase(loan.monthlyInstallment || 0, loan.currency || 'SGD'))}</div>
                                            </div>
                                        </div>

                                        <div className="progress-bar">
                                            <div className="progress-fill" style={{ width: `${progress}%` }} />
                                        </div>
                                        <div className="progress-text">
                                            <span>{progress.toFixed(1)}% paid</span>
                                            <span>{loan.settlementDate}</span>
                                        </div>

                                        {/* ── NG-2: Payment action row ── */}
                                        <div className="loan-payment-row" onClick={e => e.stopPropagation()}>
                                            {currentMonthPaid ? (
                                                <div className="payment-done-badge">
                                                    ✅ {currentPeriod} instalment recorded
                                                </div>
                                            ) : (
                                                <>
                                                    {new Date().getDate() > 10 && (
                                                        <div className="payment-warning-badge">
                                                            ⚠️ {currentPeriod} instalment not yet recorded
                                                        </div>
                                                    )}
                                                    <button
                                                        className="mark-paid-btn"
                                                        onClick={e => { e.stopPropagation(); handleMarkLoanPaid(loan.id); }}
                                                    >
                                                        ✓ Mark {loan.currency} {loan.monthlyInstallment.toLocaleString()} Paid
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                        {/* ── NG-2: Payment history ledger toggle ── */}
                                        <button
                                            className="loan-history-toggle"
                                            onClick={e => {
                                                e.stopPropagation();
                                                setExpandedLoanId(isHistoryOpen ? null : loan.id);
                                            }}
                                        >
                                            {isHistoryOpen ? '▲ Hide History' : '▼ Payment History'} ({(loan.paymentHistory || []).length} entries)
                                        </button>

                                        {/* ── NG-2: Payment history ledger panel ── */}
                                        {isHistoryOpen && (
                                            <div className="loan-history-panel" onClick={e => e.stopPropagation()}>
                                                {(loan.paymentHistory || []).length === 0 ? (
                                                    <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'center', padding: '8px 0' }}>No payments recorded yet</div>
                                                ) : (
                                                    [...(loan.paymentHistory || [])].reverse().map((p, i) => (
                                                        <div key={i} className="loan-history-row">
                                                            <span className="lh-period">{p.period}</span>
                                                            <span className="lh-date">{p.date}</span>
                                                            <span className="lh-amount">{loan.currency} {p.amount.toLocaleString()}</span>
                                                            <span className={`lh-type lh-${p.type}`}>{p.type.toUpperCase()}</span>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </>
            )}

            {activeModule === 'gold-loans' && (
                <div className="section-container">
                    <div className="section-header">
                        <div>
                            <div className="section-title">Gold Pawning</div>
                            <div className="section-subtitle">{filteredGoldLoans.length} active</div>
                        </div>
                        <button
                            className="add-btn"
                            onClick={() => {
                                setModalType('gold-loan');
                                setShowAddModal(true);
                            }}
                        >
                            + Add
                        </button>
                    </div>

                    {filteredGoldLoans.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">🏆</div>
                            <div className="empty-text">No gold pawning loans found</div>
                        </div>
                    ) : (
                        filteredGoldLoans.map(loan => {
                            const totalOwed = loan.principalRemaining + loan.unpaidInterest;
                            // NG-2: derive whether this month's interest has been recorded
                            const currentPeriod = new Date().toISOString().slice(0, 7);
                            const currentMonthPaid = (loan.paymentHistory || []).some(p => p.period === currentPeriod);
                            const isHistoryOpen = expandedLoanId === loan.id;

                            return (
                                <div
                                    key={loan.id}
                                    className="item-card"
                                    onClick={() => {
                                        setModalType('gold-loan');
                                        setEditingItem(loan);
                                        setShowEditModal(true);
                                    }}
                                >
                                    <div className="gold-owner">
                                        <div className="owner-avatar">{loan.owner.charAt(0)}</div>
                                        <div className="owner-info">
                                            <div className="owner-name">{loan.owner}</div>
                                            <div className="owner-bank">🏦 {loan.bank}</div>
                                        </div>
                                        <div className="card-badge">💍 {loan.goldWeight}g</div>
                                    </div>

                                    <div className="card-header">
                                        <div>
                                            <div className="card-title">{loan.name}</div>
                                            <div className="card-subtitle">Gold Pawning Loan</div>
                                        </div>
                                    </div>

                                    <div className="card-details">
                                        <div className="detail-item">
                                            <div className="detail-label">Borrowed</div>
                                            <div className="detail-value">₨{loan.totalAmount.toLocaleString()}</div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Monthly Rate</div>
                                            <div className="detail-value">{loan.monthlyRate}%</div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Unpaid Interest</div>
                                            <div className="detail-value">₨{loan.unpaidInterest.toLocaleString()}</div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Total Owed</div>
                                            <div className="detail-value">₨{totalOwed.toLocaleString()}</div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">≈ {baseCurrencyLabel} Owed</div>
                                            <div className="detail-value highlight">{fmtBase(toBase(totalOwed, loan.currency || 'LKR'))}</div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">≈ {baseCurrencyLabel} Monthly</div>
                                            <div className="detail-value">{fmtBase(toBase(loan.monthlyInterest || 0, loan.currency || 'LKR'))}</div>
                                        </div>
                                    </div>

                                    {/* ── NG-2: Payment action row for gold loans ── */}
                                    <div className="loan-payment-row" onClick={e => e.stopPropagation()}>
                                        {currentMonthPaid ? (
                                            <div className="payment-done-badge">
                                                ✅ {currentPeriod} interest recorded
                                            </div>
                                        ) : (
                                            <>
                                                {new Date().getDate() > 10 && (
                                                    <div className="payment-warning-badge">
                                                        ⚠️ {currentPeriod} interest not yet recorded
                                                    </div>
                                                )}
                                                <button
                                                    className="mark-paid-btn"
                                                    onClick={e => { e.stopPropagation(); handleMarkGoldLoanPaid(loan.id, loan.monthlyInterest); }}
                                                >
                                                    ✓ Mark ₨{(loan.monthlyInterest || 0).toLocaleString()} Interest Paid
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    {/* ── NG-2: Payment history ledger toggle ── */}
                                    <button
                                        className="loan-history-toggle"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setExpandedLoanId(isHistoryOpen ? null : loan.id);
                                        }}
                                    >
                                        {isHistoryOpen ? '▲ Hide History' : '▼ Payment History'} ({(loan.paymentHistory || []).length} entries)
                                    </button>

                                    {/* ── NG-2: Payment history ledger panel ── */}
                                    {isHistoryOpen && (
                                        <div className="loan-history-panel" onClick={e => e.stopPropagation()}>
                                            {(loan.paymentHistory || []).length === 0 ? (
                                                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'center', padding: '8px 0' }}>No payments recorded yet</div>
                                            ) : (
                                                [...(loan.paymentHistory || [])].reverse().map((p, i) => (
                                                    <div key={i} className="loan-history-row">
                                                        <span className="lh-period">{p.period}</span>
                                                        <span className="lh-date">{p.date}</span>
                                                        <span className="lh-amount">₨{p.amount.toLocaleString()}</span>
                                                        <span className={`lh-type lh-${p.type}`}>{p.type.toUpperCase()}</span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {activeModule === 'cards' && (
                <>
                    <div className="search-container">
                        <div className="search-wrapper">
                            <span className="search-icon">🔍</span>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search credit cards..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    className="clear-search visible"
                                    onClick={() => setSearchQuery('')}
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="section-container">
                        <div className="section-header">
                            <div>
                                <div className="section-title">Credit Cards</div>
                                <div className="section-subtitle">{filteredCreditCards.length} cards</div>
                            </div>
                            <button
                                className="add-btn"
                                onClick={() => {
                                    setModalType('credit-card');
                                    setShowAddModal(true);
                                }}
                            >
                                + Add
                            </button>
                        </div>

                        {/* Summary Card */}
                        <div className="summary-card" style={{ marginBottom: '20px' }}>
                            <div className="summary-card-title">💳 Total Outstanding</div>
                            <div className="summary-stats">
                                <div className="summary-stat">
                                    <div className="summary-stat-value">{fmtBase(cardTotals.totalBase)}</div>
                                    <div className="summary-stat-label">{baseCurrencyLabel} Total</div>
                                </div>
                                <div className="summary-stat">
                                    <div className="summary-stat-value">${cardTotals.totalSGD.toLocaleString()}</div>
                                    <div className="summary-stat-label">SGD</div>
                                </div>
                                <div className="summary-stat">
                                    <div className="summary-stat-value">${cardTotals.totalUSD.toLocaleString()}</div>
                                    <div className="summary-stat-label">USD</div>
                                </div>
                            </div>
                        </div>

                        {filteredCreditCards.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">💳</div>
                                <div className="empty-text">No credit cards found</div>
                            </div>
                        ) : (
                            filteredCreditCards.map(card => (
                                <div
                                    key={card.id}
                                    className="item-card"
                                    onClick={() => {
                                        setModalType('credit-card');
                                        setEditingItem(card);
                                        setShowEditModal(true);
                                    }}
                                >
                                    <div className="card-header">
                                        <div>
                                            <div className="card-title">{card.cardName}</div>
                                            <div className="card-subtitle">{card.bank} • {card.country}</div>
                                        </div>
                                        <div className="card-badge">{card.currency}</div>
                                    </div>

                                    <div className="card-details">
                                        <div className="detail-item">
                                            <div className="detail-label">Card Number</div>
                                            <div className="detail-value">{card.cardNumber}</div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Due Date</div>
                                            <div className="detail-value">{card.dueDate}</div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">SGD Balance</div>
                                            <div className="detail-value">${card.balanceSGD.toLocaleString()}</div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">USD Balance</div>
                                            <div className="detail-value">${card.balanceUSD.toLocaleString()}</div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">LKR Balance</div>
                                            <div className="detail-value">₨{card.balanceLKR.toLocaleString()}</div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">≈ {baseCurrencyLabel} Equiv.</div>
                                            <div className="detail-value highlight">
                                                {fmtBase(toBase(card[`balance${card.currency || 'SGD'}`] || 0, card.currency || 'SGD'))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}
        </>
    );
}